"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/hooks/useAuth";
import { getPointHistory, getPointBalance } from "@/lib/api/points";
import type { PointTransaction } from "@/lib/types";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";

function typeLabel(type: string) {
  switch (type) {
    case "signup":
      return "회원가입";
    case "trade_complete":
      return "거래 완료";
    case "review":
      return "후기 작성";
    case "purchase":
      return "사용";
    case "admin":
      return "운영";
    default:
      return "포인트";
  }
}

export default function PointsPage() {
  const { user, loading } = useAuth();
  const [pointHistory, setPointHistory] = useState<PointTransaction[]>([]);
  const [pointBalance, setPointBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    void loadPointData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const loadPointData = async () => {
    if (!user?.uid) return;
    setIsLoading(true);
    try {
      const [balance, history] = await Promise.all([
        getPointBalance(user.uid),
        getPointHistory(user.uid),
      ]);
      setPointBalance(balance);
      setPointHistory(history);
    } catch (error) {
      console.error("포인트 데이터 로드 실패:", error);
      try {
        setPointBalance(await getPointBalance(user.uid));
      } catch {
        setPointBalance(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-sm text-gray-600">로그인이 필요합니다</p>
        <Link href="/auth/login">
          <Button>로그인하기</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-950">포인트</h1>
        <p className="mt-1 text-sm text-gray-500">
          적립·사용 내역을 확인할 수 있습니다.
        </p>
      </div>

      {/* 잔액 — 작은 한 줄 카드 */}
      <section className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-gray-500">사용 가능 포인트</p>
            <p className="mt-1 text-2xl font-bold tracking-tight text-gray-950">
              {(pointBalance ?? 0).toLocaleString()}
              <span className="ml-1 text-sm font-semibold text-gray-500">P</span>
            </p>
          </div>
          <p className="max-w-[14rem] text-right text-[11px] leading-relaxed text-gray-400">
            회원가입 포인트는 AI 분석에만 사용 가능하며, 출금·프로 피드백에는
            사용할 수 없습니다.
          </p>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-xs font-semibold tracking-wide text-gray-400">
            적립 안내
          </h2>
        </div>
        <ul className="divide-y divide-gray-100 text-sm text-gray-700">
          <li className="flex items-center justify-between px-4 py-3">
            <span>회원가입 (AI 분석 전용)</span>
            <span className="font-medium text-gray-900">5,000P</span>
          </li>
          <li className="flex items-center justify-between px-4 py-3">
            <span>후기 작성</span>
            <span className="font-medium text-gray-900">50P</span>
          </li>
        </ul>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-xs font-semibold tracking-wide text-gray-400">
            사용 내역
          </h2>
        </div>

        {pointHistory.length === 0 ? (
          <div className="px-4 py-14 text-center text-sm text-gray-400">
            사용 내역이 없습니다
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {pointHistory.map((transaction, index) => {
              const isPositive = transaction.amount > 0;
              return (
                <li
                  key={transaction.id || index}
                  className="flex items-start justify-between gap-3 px-4 py-3.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {transaction.description || typeLabel(transaction.type)}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {new Date(transaction.createdAt).toLocaleDateString(
                        "ko-KR",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p
                      className={`text-sm font-semibold ${
                        isPositive ? "text-emerald-600" : "text-gray-900"
                      }`}
                    >
                      {isPositive ? "+" : ""}
                      {transaction.amount.toLocaleString()}P
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-400">
                      잔액 {transaction.balance.toLocaleString()}P
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
